import React from "react";
import { Tooltip } from "@material-ui/core";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CloseIcon,
  SectionCard,
  Field,
  Checkbox,
  InfoOutlinedIcon,
} from "../../ui-kit";
import { DocumentField, RejectedCard, RejectedFieldRow } from "..";
import Loader from "common/Loader";
import RejectionPanel from "../RejectionPanel";
import { DriverSection, PortalFilterElement, RejectionReason, SectionField } from "../../redux/onBoardingSlice.types";
import { useStyles } from "./DriverVerificationPanel.styles";
import { DOC_STATE, SECTION_STATUS } from "./constants";
import { RejectionPayload, SectionTrackingState } from "./types";
import { useDriverVerificationPanel } from "./useDriverVerificationPanel";
import { ONBOARDING_TEXT } from "../text.constants";

export type { DocumentItem } from "../types";
export type { RejectionPayload } from "./types";

// ── Types ─────────────────────────────────────────────────────────────────

export interface DriverVerificationPanelProps {
  rowData: PortalFilterElement;
  sections: DriverSection[];
  sectionsLoading: boolean;
  onClose: () => void;
  onReject: (payload: RejectionPayload) => void;
  onApprove: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const DriverVerificationPanel = ({
  rowData,
  sections,
  sectionsLoading,
  onClose,
  onReject,
  onApprove,
}: DriverVerificationPanelProps) => {
  const classes = useStyles();

  const {
    scrollAreaRef,
    sectionRefs,
    sortedSections,
    sectionStates,
    sectionPickerRefs,
    docStates,
    sasUrls,
    docLoadingMap,
    makeDocHandlers,
    canApprove,
    canReject,
    handleFooterReject,
    handleSectionReject,
    handleSectionToggleField,
    handleSectionSelectAllFields,
    handleSectionRejectConfirm,
    handleSectionRejectCancel,
    handleSectionSelectReason,
    handleSectionUnreject,
  } = useDriverVerificationPanel({ sections, onReject });

  // ── Field rendering ───────────────────────────────────────────────────

  /** True when a field has a real value to show — used to hide the label/heading
   * entirely for null, undefined, or blank values instead of rendering a "-". */
  const hasFieldValue = (value: string | null | undefined): boolean =>
    value !== null && value !== undefined && value.trim() !== "";

  /** A section is worth rendering only if at least one of its displayable
   * (text/image) fields has a real value. */
  const sectionHasValue = (section: DriverSection): boolean =>
    section.fields.some((f) => (f.type === "text" || f.type === "image") && hasFieldValue(f.value));

  /** A field is rejected iff its key is present in rejectedFields — checking the reason
   * string's truthiness would wrongly treat an empty-string reason as "not rejected". */
  const isFieldRejected = (sState: SectionTrackingState | undefined, key: string | null | undefined) =>
    !!key && !!sState?.rejectedFields && key in sState.rejectedFields;

  /** Wraps `content` with a selection checkbox while a section's reject picker is open.
   * `fieldLabel` gives the checkbox an accessible name (aria-label) so screen-reader
   * users know which field/image it selects, instead of hearing an unlabeled checkbox. */
  const renderSelectable = (
    fieldKey: string,
    fieldLabel: string,
    content: React.ReactNode,
    sectionId: string,
    sState?: SectionTrackingState
  ) => {
    const checked = sState?.selectedFieldKeys.includes(fieldKey) ?? false;
    return (
      <Box
        className={`${classes.selectableField}${checked ? ` ${classes.selectableFieldSelected}` : ""}`}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={() => handleSectionToggleField(sectionId, fieldKey)}
          className={classes.selectCheckbox}
          inputProps={{ "aria-label": ONBOARDING_TEXT.DVP_SELECT_FIELD_ARIA(fieldLabel) }}
        />
        <Box className={classes.selectableFieldContent}>{content}</Box>
      </Box>
    );
  };

  const renderFields = (
    section: DriverSection,
    rejectionReasons: RejectionReason[],
    sState?: SectionTrackingState
  ) => {
    const isEditing = sState?.showRejectPicker === true;
    const result: JSX.Element[] = [];
    let textBuffer: SectionField[] = [];

    const renderTextField = (field: SectionField) => {
      if (isFieldRejected(sState, field.key)) {
        return (
          <RejectedFieldRow
            key={field.fieldId}
            label={field.label ?? ""}
            value={field.value ?? "-"}
            onUndo={() => handleSectionUnreject(section.sectionId, field.key!)}
          />
        );
      }
      const content = <Field label={field.label ?? ""} value={field.value ?? "-"} />;
      if (isEditing && field.key) {
        return (
          <Box key={field.fieldId} style={{ flex: "1 1 0", minWidth: 0 }}>
            {renderSelectable(field.key, field.label ?? field.key, content, section.sectionId, sState)}
          </Box>
        );
      }
      return <Box key={field.fieldId} style={{ flex: "1 1 0", minWidth: 0 }}>{content}</Box>;
    };

    const flushText = (key: string) => {
      if (!textBuffer.length) return;
      result.push(
        <Box key={key} className={classes.fieldsRow}>
          {textBuffer.map(renderTextField)}
        </Box>
      );
      textBuffer = [];
    };

    section.fields.forEach((field, idx) => {
      if (!field.type || !field.key) return;
      if (field.type === "text") {
        if (!hasFieldValue(field.value) && !isFieldRejected(sState, field.key)) return;
        textBuffer.push(field);
        if (textBuffer.length === 2) flushText(`row-${idx}`);
      } else if (field.type === "image" && field.value) {
        flushText(`before-img-${idx}`);

        if (isFieldRejected(sState, field.key)) {
          result.push(
            <RejectedCard
              key={field.fieldId}
              label={field.label || field.fieldId}
              fileName={field.fieldId}
              thumbnailUrl={field.value}
              onUndo={() => handleSectionUnreject(section.sectionId, field.key!)}
            />
          );
          return;
        }

        const docField = (
          <DocumentField
            key={field.fieldId}
            doc={{
              label: field.label || field.fieldId,
              fileName: field.fieldId,
              thumbnailUrl: field.value,
              previewUrls: sasUrls[field.fieldId] ? [sasUrls[field.fieldId]] : [],
            }}
            rejectionReasons={rejectionReasons}
            docState={
              docLoadingMap[field.fieldId]
                ? { ...docStates[field.fieldId], state: DOC_STATE.COLLAPSED }
                : docStates[field.fieldId]
            }
            {...makeDocHandlers(field.fieldId, field.value, rejectionReasons)}
          />
        );

        if (isEditing && field.key) {
          result.push(
            <React.Fragment key={field.fieldId}>
              {renderSelectable(field.key, field.label || field.fieldId, docField, section.sectionId, sState)}
            </React.Fragment>
          );
        } else {
          result.push(docField);
        }
      }
    });
    flushText("tail");
    return result;
  };

  // ── Section rendering ─────────────────────────────────────────────────

  const renderSection = (section: DriverSection) => {
    const sState = sectionStates[section.sectionId];
    const showPicker = sState?.showRejectPicker;
    const rejectionReasons = section.rejectionReasons ?? [];
    const rejectedCount = Object.keys(sState?.rejectedFields ?? {}).length;
    const allRejected = sState?.status === SECTION_STATUS.REJECTED;

    const sectionImageFields = section.fields.filter((f) => f.type === "image" && f.key && f.value);
    const rejectDisabled =
      sectionImageFields.length > 0 &&
      !sectionImageFields.every((f) => docStates[f.fieldId]?.viewed === true);

    // Fields currently rendered with a selection checkbox: not already rejected,
    // and either a text field or an image field with a value.
    const selectableFieldKeys = section.fields
      .filter((f) => f.key && !isFieldRejected(sState, f.key) && (f.type === "text" || (f.type === "image" && f.value)))
      .map((f) => f.key!);
    const allFieldsSelected =
      selectableFieldKeys.length > 0 &&
      selectableFieldKeys.every((k) => sState?.selectedFieldKeys.includes(k));

    const uniqueReasons = Array.from(new Set(Object.values(sState?.rejectedFields ?? {})));

    let headerAction: React.ReactNode = null;
    if (rejectedCount > 0) {
      headerAction = (
        <Box className={classes.rejectedBanner}>
          <Typography className={classes.rejectedBannerText}>
            {allRejected ? ONBOARDING_TEXT.DVP_ALL_REJECTED_TEXT : ONBOARDING_TEXT.DVP_FIELDS_REJECTED_COUNT(rejectedCount)}
          </Typography>
          <Tooltip title={uniqueReasons.join(", ")} placement="top" arrow>
            <span style={{ display: "inline-flex", alignItems: "center", color: "inherit", cursor: "pointer", flexShrink: 0 }}>
              <InfoOutlinedIcon style={{ fontSize: 14 }} />
            </span>
          </Tooltip>
          <Button
            size="small"
            className={classes.undoRejectBtn}
            onClick={() => handleSectionUnreject(section.sectionId)}
            disableRipple
          >
            {ONBOARDING_TEXT.DVP_UNDO_ALL_BTN}
          </Button>
        </Box>
      );
    } else if (!showPicker) {
      headerAction = (
        <Tooltip
          title={rejectDisabled ? ONBOARDING_TEXT.DVP_REJECT_TOOLTIP : ""}
          arrow
          placement="top"
        >
          <span style={{ cursor: rejectDisabled ? "not-allowed" : "default" }}>
            <Button
              size="small"
              variant="outlined"
              className={classes.rejectSectionBtn}
              disabled={rejectDisabled}
              onClick={() => handleSectionReject(section.sectionId)}
              disableRipple
              style={rejectDisabled ? { pointerEvents: "none" } : undefined}
            >
              {ONBOARDING_TEXT.DVP_REJECT_BTN}
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <div
        key={section.id}
        ref={(el) => { sectionRefs.current[section.sectionId] = el; }}
        data-section-id={section.sectionId}
      >
        <SectionCard title={section.title} subtitle={section.description} headerAction={headerAction}>
          {showPicker && (
            <Box className={classes.selectHintRow}>
              <Typography className={classes.selectHintText}>
                {ONBOARDING_TEXT.DVP_SELECT_FIELDS_HINT}
              </Typography>
              <Box className={classes.selectHintActions}>
                <Typography className={classes.selectHintCount}>
                  {ONBOARDING_TEXT.DVP_FIELDS_SELECTED_COUNT(sState?.selectedFieldKeys.length ?? 0)}
                </Typography>
                <Typography className={classes.selectHintDivider} aria-hidden>
                  {"·"}
                </Typography>
                <Button
                  size="small"
                  className={classes.selectAllBtn}
                  onClick={() => handleSectionSelectAllFields(section.sectionId, selectableFieldKeys)}
                  disableRipple
                >
                  {allFieldsSelected ? ONBOARDING_TEXT.DVP_CLEAR_ALL_BTN : ONBOARDING_TEXT.DVP_SELECT_ALL_BTN}
                </Button>
              </Box>
            </Box>
          )}

          {renderFields(section, rejectionReasons, sState)}

          {showPicker && (
            <div ref={(el) => { sectionPickerRefs.current[section.sectionId] = el; }}>
              <Box className={classes.sectionRejectRow}>
                <RejectionPanel
                  reasons={rejectionReasons}
                  selectedReason={sState?.pendingReason ?? ""}
                  disableConfirm={(sState?.selectedFieldKeys.length ?? 0) === 0}
                  onSelect={(reason) => handleSectionSelectReason(section.sectionId, reason)}
                  onCancel={() => handleSectionRejectCancel(section.sectionId)}
                  onConfirm={() => handleSectionRejectConfirm(section.sectionId)}
                  className={classes.inlinePicker}
                />
              </Box>
            </div>
          )}
        </SectionCard>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box className={classes.panelRoot}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography variant="h6" className={classes.headerTitle}>
            {ONBOARDING_TEXT.DVP_HEADER_TITLE_PREFIX} {rowData.userName}
          </Typography>
          <Chip label={rowData.status} size="small" className={classes.statusBadge} />
        </Box>
        <IconButton className={classes.closeButton} onClick={onClose} aria-label={ONBOARDING_TEXT.DVP_CLOSE_ARIA} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <div ref={scrollAreaRef} className={classes.scrollArea}>
        {sectionsLoading ? <Loader /> : sortedSections.filter(sectionHasValue).map(renderSection)}
      </div>

      <Box className={classes.footer}>
        <Button
          variant="contained"
          className={classes.footerRejectBtn}
          onClick={handleFooterReject}
          disabled={!canReject}
          disableElevation
        >
          {ONBOARDING_TEXT.DVP_FOOTER_REJECT_BTN}
        </Button>
        <Button
          variant="contained"
          className={classes.footerApproveBtn}
          onClick={() => canApprove && onApprove()}
          disabled={!canApprove}
          disableElevation
        >
          {ONBOARDING_TEXT.DVP_FOOTER_APPROVE_BTN}
        </Button>
      </Box>
    </Box>
  );
};

export default DriverVerificationPanel;
